import React, { useEffect, useState } from 'react'
import AddressForm from '@/components/forms/address-step/AddressForm'
import { IFormValues } from '@/components/forms/address-step/FormAddress'
import FormHeader from '@/components/layouts/form-layout/FormHeader'
import { useGetAddress } from '@/api/address/GetAddress'
import { useSubmitAddress } from '@/api/create-address/CreateAddress'
import { useUpdateAddress } from '@/api/update-address/UpdateAddress'
import { useGetStepCorrections } from '@/api/step-correction/StepCorrection'
import { toast } from 'react-toastify'
import { TPostApiRequestType } from '@/api/create-address/type'
import { useNavigate } from 'react-router-dom'
import {
  mapApiDataToFormValues,
  getPatchData
} from '@/utils/helpers/addressUtils'
import Alert from '@dgshahr/ui-kit/Alert'
import { IGetStepCorrectionsResponseType } from '@/api/step-correction/type'
import useAuthStore from '@/store/useAuthStore'
import faToEnNum from '@/utils/functions/convertNumbers'

const Address = () => {
  const { data, isLoading } = useGetAddress({})
  const { data: correction } = useGetStepCorrections({ step: 'ADDRESS' })

  const { mutate: submitAddress } = useSubmitAddress({})
  const { mutate: updateAddress } = useUpdateAddress({})
  const { setLastStep } = useAuthStore()
  const navigate = useNavigate()

  const [formValues, setFormValues] = useState<IFormValues>({
    provinces: null,
    cities: null,
    address: '',
    detail: '',
    postalCode: '',
    lat: 35.6997,
    lng: 51.3408,
    is_warehouse: false,
    warehouseProvinces: null,
    warehouseCities: null,
    warehouseAddress: '',
    warehouseDetail: '',
    warehousePostalCode: '',
    warehouseLat: 35.6997,
    warehouseLng: 51.3408
  })

  useEffect(() => {
    if (data) {
      setFormValues(mapApiDataToFormValues(data))
    }
  }, [data])

  const descriptions = correction?.map(
    (item: IGetStepCorrectionsResponseType) => item.description
  )

  const handleFormSubmit = (values: IFormValues) => {
    const isExistingCompanyAddress = !!data?.company_address
    const hasCorrection = correction && correction.length > 0

    const normalizedValues: IFormValues = {
      ...values,
      address: faToEnNum(values.address),
      detail: faToEnNum(values.detail),
      postalCode: faToEnNum(values.postalCode),
      warehouseAddress: faToEnNum(values.warehouseAddress),
      warehouseDetail: faToEnNum(values.warehouseDetail),
      warehousePostalCode: faToEnNum(values.warehousePostalCode)
    }

    const patchData = getPatchData(data, normalizedValues)

    const navigateNext = () => {
      navigate(
        hasCorrection
          ? '/merchant-register/status'
          : '/merchant-register/document-upload'
      )
    }

    const postRequestData: TPostApiRequestType = {
      company_address: {
        postal_code: normalizedValues.postalCode,
        address: normalizedValues.address,
        details: normalizedValues.detail,
        lat: normalizedValues.lat,
        lng: normalizedValues.lng,
        is_warehouse: normalizedValues.is_warehouse,
        city: normalizedValues.cities
      },
      ...(normalizedValues.is_warehouse && {
        warehouse_address: {
          postal_code: normalizedValues.warehousePostalCode,
          address: normalizedValues.warehouseAddress,
          details: normalizedValues.warehouseDetail,
          lat: normalizedValues.warehouseLat,
          lng: normalizedValues.warehouseLng,
          is_warehouse: true,
          city: normalizedValues.warehouseCities
        }
      })
    }

    const noChange =
      isExistingCompanyAddress && Object.keys(patchData).length === 0
    const noNewData =
      !isExistingCompanyAddress &&
      !normalizedValues.address &&
      !normalizedValues.is_warehouse

    if (noChange || noNewData) {
      navigateNext()
      return
    }

    const onSuccess = () => {
      setFormValues(normalizedValues)
      setLastStep(3)
      navigateNext()
    }

    const onError = () => {
      toast.error(
        isExistingCompanyAddress
          ? 'خطا در به‌روزرسانی آدرس‌'
          : 'خطا در ثبت آدرس'
      )
    }

    if (isExistingCompanyAddress) {
      updateAddress(patchData, { onSuccess, onError })
    } else {
      submitAddress(postRequestData, { onSuccess, onError })
    }
  }

  if (isLoading) {
    return (
      <>
        <FormHeader title="ثبت‌نام فروشنده" />
        <div className="flex h-full items-center justify-center">
          <div className="custome-dot-flashing" />
        </div>
      </>
    )
  }

  return (
    <>
      <FormHeader title="ثبت‌نام فروشنده" />
      {correction?.length > 0 && (
        <div className="flex flex-col gap-4 mb-7">
          <Alert
            size="small"
            title="اصلاحیه ها"
            variant="error"
            text={descriptions.map((desc: string) => (
              <React.Fragment key={desc}>
                {desc}
                <br />
              </React.Fragment>
            ))}
          />
        </div>
      )}
      <div className="mt-5">
        <AddressForm initialValues={formValues} onSubmit={handleFormSubmit} />
      </div>
    </>
  )
}

export default Address
